import {
  Box,
  Divider,
  Flex,
  FlexProps,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  IconButton,
  List,
  ListItem,
} from "@chakra-ui/react";
import { useSelect } from "downshift";
import React from "react";
import { RiArrowDownSLine, RiCloseLine } from "react-icons/ri";

const DividerSymbol: unique symbol = Symbol("ItemDivider");
type ItemDividerType = { readonly [DividerSymbol]: true };
export const ItemDivider: ItemDividerType = { [DividerSymbol]: true };
export type Item = { key: string; disabled?: boolean };
export type CustomSelectProps<T extends Item> = {
  disabled?: boolean;
  label: React.ReactNode;
  helperText?: string;
  items: (T | ItemDividerType)[];
  selectedItem: T | null;
  onChange: (item: T | null) => void;
  onBlur?: () => void;
  renderItem: (item: T) => React.ReactNode;
  renderSelectedItem: (item: T | null) => React.ReactNode;
  renderEmptyItems?: () => React.ReactNode;
  error?: string;
  alert?: React.ReactNode;
  /*
    Some cases the dropdown needs to be open up,
    because it would break the page layout or create a unecessary scroll.
  */
  openDirection?: "up" | "down";
  maxHeight?: string;
  leftIcon?: React.ReactNode;
  hideClearButton?: boolean;
  isRequired?: boolean;
};
/**
 * Custom Select component capable of rendering custom option.
 */
export function CustomSelect<T extends Item>(props: CustomSelectProps<T>) {
  const {
    items,
    onChange,
    onBlur,
    renderItem,
    renderSelectedItem,
    error,
    helperText,
    disabled,
    renderEmptyItems,
    alert,
    openDirection = "down",
    leftIcon,
    hideClearButton = true,
    isRequired = false,
  } = props;

  const selectableItems = React.useMemo(() => items.filter(isItem), [items]);

  const itemToString = React.useCallback((value: T | null) => {
    return value ? value.key : "";
  }, []);
  const {
    selectedItem,
    selectItem,
    getItemProps,
    getLabelProps,
    getToggleButtonProps,
    isOpen,
    getMenuProps,
    highlightedIndex,
  } = useSelect<T>({
    items: selectableItems,
    itemToString,
    selectedItem: props.selectedItem,
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) =>
      onChange(newSelectedItem ?? null),
    isItemDisabled: (item) => item.disabled ?? false,
  });

  const errorStyle: FlexProps =
    !disabled && error
      ? {
          borderWidth: "1px",
          borderColor: "red.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
        }
      : {
          borderWidth: "1px",
          borderColor: "inherit",
        };

  const disabledStyle: FlexProps = disabled
    ? {
        cursor: "not-allowed",
        opacity: 0.4,
      }
    : {};

  const focusStyle: FlexProps = disabled
    ? {}
    : {
        outline: "0px",
        border: "1px solid",
        borderColor: "blue.500",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
      };

  const handleBlur = React.useCallback(
    (event: React.FocusEvent) => {
      // Only trigger onBlur if the focus is not moving to an element within the select
      if (!event.currentTarget.contains(event.relatedTarget as Node)) {
        onBlur?.();
      }
    },
    [onBlur]
  );

  return (
    <FormControl
      isInvalid={!disabled && error !== undefined}
      isDisabled={disabled}
      isRequired={isRequired}
    >
      <FormLabel {...getLabelProps()}>{props.label}</FormLabel>
      {alert && <Box marginBottom={2}> {alert}</Box>}
      <Flex
        borderStyle="solid"
        borderRadius={6}
        padding={2}
        paddingLeft={4}
        justifyContent="space-between"
        alignItems="center"
        transitionProperty="border-color,box-shadow"
        transitionDuration="0.2s"
        gap="2"
        _focus={focusStyle}
        {...getToggleButtonProps({
          disabled,
          onBlur: handleBlur,
        })}
        {...errorStyle}
        {...disabledStyle}
      >
        {leftIcon ? leftIcon : null}
        <Box flexGrow="1">
          {items.length === 0 && renderEmptyItems
            ? renderEmptyItems()
            : renderSelectedItem(selectedItem)}
        </Box>
        {!hideClearButton && selectedItem !== null ? (
          <IconButton
            aria-label="Clear selection"
            icon={<RiCloseLine />}
            minWidth={5}
            boxSize={5}
            width={1}
            variant="ghost"
            _hover={{ bg: "none" }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              selectItem(null);
              onChange(null);
            }}
          />
        ) : null}
        <Icon as={RiArrowDownSLine} boxSize={6} />
      </Flex>
      <List
        {...getMenuProps()}
        paddingY={2}
        borderColor="inherit"
        borderWidth={1}
        borderStyle="solid"
        display={isOpen ? "flex" : "none"}
        position="absolute"
        bg="white"
        width="100%"
        borderRadius={4}
        flexDirection="column"
        boxShadow="lg"
        bottom={
          openDirection === "up" ? (props.label ? "66%" : "100%") : undefined
        }
        maxHeight={props.maxHeight}
        overflow={props.maxHeight ? "scroll" : undefined}
        zIndex={10000}
      >
        {isOpen &&
          (() => {
            // Track a separate index for non-divider items to stay in sync
            // with downshift's internal indices, which are based on the
            // filtered items array (dividers excluded).
            let itemIndex = 0;
            return items.map((item, index) => {
              if (isDivider(item)) {
                return <Divider key={`divider-${index}`} />;
              }

              const currentItemIndex = itemIndex;
              itemIndex++;

              return (
                <ListItem
                  key={item.key}
                  paddingX={4}
                  paddingY={2}
                  transition="background 0.2s"
                  background={
                    highlightedIndex === currentItemIndex
                      ? "gray.200"
                      : selectedItem === item
                      ? "gray.100"
                      : undefined
                  }
                  {...getItemProps({ item, index: currentItemIndex })}
                  {...(item.disabled ? { opacity: 0.4 } : {})}
                >
                  {renderItem(item)}
                </ListItem>
              );
            });
          })()}
      </List>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}

export function isItem<T extends Item>(item: T | ItemDividerType): item is T {
  return !isDivider(item);
}

function isDivider<T extends Item>(
  item: T | ItemDividerType
): item is ItemDividerType {
  return DividerSymbol in item;
}
